import type { RequiredDeep } from "type-fest";
import { type BuildingLine, type BuildingZone, type CheckZone, type ExcludedZone, type ProjectData, RoofType } from "../../schemas/models";

const ZERO_GUID = "00000000-0000-0000-0000-000000000000";

const ZONE_COLORS = {
	CheckZone: "DarkGray",
	BuildingZone: "237, 237, 238",
	ExcludedZone: "Red",
} as const;

const DEFAULT_PROJECT_DATA_PARAMETERS: RequiredDeep<ProjectData> = {
	ProjectName: "Default Project",
	IsDowntown: false,
	TrueNorth: { AngleBetweenWorldYAndTrueNorth: 0 },
	AnalyzeObstruction: true,
	AnalyzeDaylight: true,
	DaylightTimeRequirements: [
		{
			TimeIntervalAvailable: { Start: new Date("2025-03-20T06:00:00Z"), End: new Date("2025-03-20T16:00:00Z") },
			TimeSpanToPass: { Milliseconds: 10800000 },
			TimeZoneInfoIdUsedInUIOnly: "Central European Standard Time",
		},
		{
			TimeIntervalAvailable: { Start: new Date("2025-09-22T05:00:00Z"), End: new Date("2025-09-22T15:00:00Z") },
			TimeSpanToPass: { Milliseconds: 10800000 },
			TimeZoneInfoIdUsedInUIOnly: "Central European Standard Time",
		},
	],
	FlatsAreaRatio: 0.9,
	MinimumDistanceBetweenBuildings: 0,
	Guid: ZERO_GUID,
	LocationOnEarth: {
		Longitude: 0,
		Latitude: 0,
	},
};

const DEFAULT_CHECK_ZONE_PARAMETERS: RequiredDeep<CheckZone> = {
	MaxBuildingCoverageRatio: 0.4,
	MaxFloorAreaRatioAboveGround: 2.0,
	MaxFloorAreaRatioUnderground: 2.0,
	Color: ZONE_COLORS.CheckZone,
	//
	Guid: ZERO_GUID,
	ParentGuid: ZERO_GUID,
	Name: "undefined",
	Boundary: null,
};

const DEFAULT_BUILDING_ZONE_PARAMETERS: RequiredDeep<BuildingZone> = {
	MaxRelativeBuildingHeight: 16.0,
	MaxEavesHeight: 10.0,
	RoofType: RoofType.Flat,
	RoofPitch: { Start: 0.0, End: 0.0 },
	BuildingNumberOfLevels: { Start: 3, End: 5 },
	GroundFloorLevelHeight: 3.0,
	FloorLevelHeight: 3.0,
	TopFloorLevelHeight: 3.0,
	BuildingLevelHeights: [3.0, 3.0, 3.0],
	BuildingAtticHeight: 1.0,
	StructureRatio: {
		Flat0Bedroom: 0.3,
		Flat1Bedroom: 0.3,
		Flat2Bedrooms: 0.3,
		Flat3Bedrooms: 0.1,
		Flat4Bedrooms: 0.0,
		OtherFlat: 0.0,
	},
	StructureIntervals: {
		Flat0Bedroom: { Start: 25.0, End: 35.0 },
		Flat1Bedroom: { Start: 26.0, End: 45.0 },
		Flat2Bedrooms: { Start: 46.0, End: 55.0 },
		Flat3Bedrooms: { Start: 56.0, End: 65.0 },
		Flat4Bedrooms: { Start: 66.0, End: 76.0 },
	},
	ParkingMultiplier: {
		Flat0Bedroom: 1.2,
		Flat1Bedroom: 1.2,
		Flat2Bedrooms: 1.2,
		Flat3Bedrooms: 1.2,
		Flat4Bedrooms: 1.2,
		OtherFlat: 1.2,
		AdditionalPlaces: 0,
		AreaOfOneParkingPlace: 35.0,
	},
	BuildingLength: { Start: 30.0, End: 50.0 },
	BuildingDepthNS: { Start: 13.0, End: 17.0 },
	BuildingDepthEW: { Start: 12.0, End: 14.0 },
	BuildingOrientationAngle: 0.0,
	BuildingOrientationAngleDeviation: Math.PI,
	Color: ZONE_COLORS.BuildingZone,
	//
	Guid: ZERO_GUID,
	ParentGuid: ZERO_GUID,
	Name: "undefined",
	Boundary: null,
};

const DEFAULT_EXCLUDED_ZONE_PARAMETERS: RequiredDeep<ExcludedZone> = {
	Color: ZONE_COLORS.ExcludedZone,
	Guid: ZERO_GUID,
	ParentGuid: ZERO_GUID,
	Name: "undefined",
	Boundary: null,
};

const DEFAULT_BUILDING_LINE_PARAMETERS: RequiredDeep<BuildingLine> = {
	Guid: ZERO_GUID,
	Shape: null,
	ParentGuid: ZERO_GUID,
};

export {
	DEFAULT_BUILDING_LINE_PARAMETERS,
	DEFAULT_BUILDING_ZONE_PARAMETERS,
	DEFAULT_CHECK_ZONE_PARAMETERS,
	DEFAULT_EXCLUDED_ZONE_PARAMETERS,
	DEFAULT_PROJECT_DATA_PARAMETERS,
	ZERO_GUID,
};
